"use client";

import type { ReactNode } from "react";
import { TrackingBoundary } from "../../../lib/sentry/index";
import { ShellTrigger } from "./shell/ResponsiveShell";

export interface RelayHistoryTriggerProps {
  /** Render the child as the trigger instead of wrapping it. Defaults to
   * `true` whenever a child is supplied, matching `RelayDepositTrigger`'s
   * (and `WalletConnectModalTrigger`'s) `asChild ?? children != null` rule. */
  asChild?: boolean;
  children?: ReactNode;
}

/**
 * Thin pass-through to `ShellTrigger` (radix `Dialog.Trigger` on desktop /
 * vaul `Drawer.Trigger` on mobile), mirroring `RelayDepositTrigger` so hosts
 * write `<RelayHistory.Trigger><button>…</button></RelayHistory.Trigger>`
 * without knowing which primitive is active. Falls back to a plain default
 * button when no child is given.
 *
 * Wrapped in `<TrackingBoundary feature="relay">` for the same reason
 * `RelayDepositTrigger`/`WalletConnectModalTrigger` are: `data-track="open"` is
 * only read by the delegate a boundary installs, and this trigger sits OUTSIDE
 * the portaled `<Content>`'s own boundary.
 */
export function RelayHistoryTrigger({ asChild, children }: RelayHistoryTriggerProps) {
  return (
    <TrackingBoundary feature="relay">
      <ShellTrigger asChild={asChild ?? children != null}>
        {children ?? (
          <button type="button" data-track="open">
            History
          </button>
        )}
      </ShellTrigger>
    </TrackingBoundary>
  );
}
